import {CollapsibleSidebar} from '@github-ui/layout-primitives/CollapsibleSidebar'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {CollapsibleFeedbackLink} from '@github-ui/repos-issues-sidebar/CollapsibleFeedbackLink'
import {SIDEBAR_STORAGE_KEY} from '@github-ui/repos-issues-sidebar/constants'
import type {CollapsibleLeftPaneConfig} from '@github-ui/three-panes-layout'
import type {ReactNode} from 'react'

/**
 * Shared sidebar configuration for every page that renders the issues sidebar
 * inside a `ThreePanesLayout`.
 *
 * - `issue_pinned_views` OFF → no sidebar (returns {}).
 * - `issue_pinned_views` ON → render the sidebar inside a `CollapsibleSidebar`
 *   with a persisted toggle so the collapse state is shared across the issues,
 *   milestones, labels and views pages.
 */
export function useRepoIssuesSidebarPanes(renderSidebar: (collapsible?: boolean) => ReactNode): {
  collapsibleLeftPane?: CollapsibleLeftPaneConfig
} {
  // eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags -- issue_pinned_views uses actor-aware server-side evaluation via PinnedViewsFeature; isFeatureEnabled only knows current_user.
  if (!useFeatureFlag('issue_pinned_views')) {
    return {}
  }

  return {
    collapsibleLeftPane: {
      storageKey: SIDEBAR_STORAGE_KEY,
      expandedWidth: 'small',
      render: () => <>{renderSidebar(true)}</>,
      renderFooter: ({isExpanded, toggle}) => (
        <>
          <CollapsibleFeedbackLink isExpanded={isExpanded} />
          <CollapsibleSidebar.Toggle isExpanded={isExpanded} onToggle={toggle} />
        </>
      ),
    },
  }
}
